/**
 * Secure Storage Wrapper
 * Encrypts all data before storing in localStorage
 * Adds integrity checking and tamper detection
 */

import {
  encryptData,
  decryptData,
  calculateHash,
  verifyIntegrity,
  createEncryptionPassphrase,
} from './encryption';

interface StorageMetadata {
  timestamp: number;
  version: string;
  hash: string;
}

interface SecureStorageItem {
  data: string; // encrypted data
  metadata: StorageMetadata;
}

class SecureStorage {
  private passphrase: string;
  private memoryCache: Map<string, any> = new Map();
  private readonly STORAGE_VERSION = '1.0';

  constructor(userSecret?: string) {
    this.passphrase = createEncryptionPassphrase(userSecret);
  }

  /**
   * Update the encryption passphrase (e.g., when user sets/changes PIN)
   */
  async updatePassphrase(newUserSecret?: string): Promise<void> {
    const oldPassphrase = this.passphrase;
    this.passphrase = createEncryptionPassphrase(newUserSecret);

    // Re-encrypt all existing data with new passphrase
    const keys = this.getAllKeys();
    for (const key of keys) {
      try {
        const data = await this.getItem(key, oldPassphrase);
        if (data !== null) {
          await this.setItem(key, data);
        }
      } catch (error) {
        console.error(`Failed to re-encrypt key ${key}:`, error);
      }
    }
  }

  /**
   * Store encrypted data with integrity check
   */
  async setItem(key: string, value: any): Promise<void> {
    try {
      // Calculate hash for integrity
      const hash = await calculateHash(value);

      // Encrypt the actual data
      const encryptedData = await encryptData(value, this.passphrase);

      // Create metadata
      const metadata: StorageMetadata = {
        timestamp: Date.now(),
        version: this.STORAGE_VERSION,
        hash: hash,
      };

      // Package everything together
      const storageItem: SecureStorageItem = {
        data: encryptedData,
        metadata: metadata,
      };

      // Store in localStorage
      localStorage.setItem(this.getSecureKey(key), JSON.stringify(storageItem));

      // Update memory cache
      this.memoryCache.set(key, value);
    } catch (error) {
      console.error('SecureStorage.setItem failed:', error);
      throw new Error(`Failed to securely store item: ${key}`);
    }
  }

  /**
   * Retrieve and decrypt data with integrity verification
   */
  async getItem(key: string, customPassphrase?: string): Promise<any> {
    try {
      // Check memory cache first
      if (this.memoryCache.has(key)) {
        return this.memoryCache.get(key);
      }

      // Retrieve from localStorage
      const storedString = localStorage.getItem(this.getSecureKey(key));
      if (!storedString) {
        return null;
      }

      // Parse storage item
      let storageItem: SecureStorageItem;
      try {
        storageItem = JSON.parse(storedString);
      } catch (parseError) {
        console.warn(`Failed to parse stored item for key: ${key}, removing corrupted data`);
        this.removeItem(key);
        return null;
      }

      // Decrypt data
      const passphrase = customPassphrase || this.passphrase;
      let decryptedData: any;
      try {
        decryptedData = await decryptData(storageItem.data, passphrase);
      } catch (decryptError) {
        console.warn(`Decryption failed for key: ${key}, removing corrupted/old data`);
        this.removeItem(key);
        return null;
      }

      // Verify integrity
      try {
        const isValid = await verifyIntegrity(
          decryptedData,
          storageItem.metadata.hash
        );

        if (!isValid) {
          console.warn(`Integrity check failed for key: ${key}, removing tampered data`);
          this.removeItem(key);
          return null;
        }
      } catch (integrityError) {
        console.warn(`Integrity verification failed for key: ${key}, removing data`);
        this.removeItem(key);
        return null;
      }

      // Update memory cache
      this.memoryCache.set(key, decryptedData);

      return decryptedData;
    } catch (error) {
      // Final catch-all: log error but return null instead of throwing
      console.warn(`SecureStorage.getItem failed for key: ${key}, returning null`, error);
      return null;
    }
  }

  /**
   * Remove item from secure storage
   */
  removeItem(key: string): void {
    localStorage.removeItem(this.getSecureKey(key));
    this.memoryCache.delete(key);
  }

  /**
   * Clear all secure storage
   */
  clear(): void {
    const keys = this.getAllKeys();
    keys.forEach(key => this.removeItem(key));
    this.memoryCache.clear();
  }

  /**
   * Get all secure storage keys
   */
  getAllKeys(): string[] {
    const prefix = this.getSecureKey('');
    const keys: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keys.push(key.substring(prefix.length));
      }
    }
    
    return keys;
  }

  /**
   * Check if key exists
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(this.getSecureKey(key)) !== null;
  }

  /**
   * Get storage metadata without decrypting
   */
  getMetadata(key: string): StorageMetadata | null {
    const storedString = localStorage.getItem(this.getSecureKey(key));
    if (!storedString) {
      return null;
    }

    try {
      const storageItem: SecureStorageItem = JSON.parse(storedString);
      return storageItem.metadata;
    } catch (error) {
      console.error('Failed to get metadata:', error);
      return null;
    }
  }

  /**
   * Export encrypted backup (for cloud sync, etc.)
   */
  async exportEncryptedBackup(): Promise<string> {
    const backup: Record<string, SecureStorageItem> = {};
    const keys = this.getAllKeys();

    for (const key of keys) {
      const storedString = localStorage.getItem(this.getSecureKey(key));
      if (storedString) {
        backup[key] = JSON.parse(storedString);
      }
    }

    return JSON.stringify(backup);
  }

  /**
   * Import encrypted backup
   */
  async importEncryptedBackup(backupString: string): Promise<void> {
    try {
      const backup: Record<string, SecureStorageItem> = JSON.parse(backupString);

      for (const [key, storageItem] of Object.entries(backup)) {
        localStorage.setItem(
          this.getSecureKey(key),
          JSON.stringify(storageItem)
        );
      }

      // Clear memory cache to force re-validation
      this.memoryCache.clear();
    } catch (error) {
      console.error('Failed to import backup:', error);
      throw new Error('Failed to import encrypted backup');
    }
  }

  /**
   * Clear memory cache (call on app sleep/background)
   */
  clearMemoryCache(): void {
    this.memoryCache.clear();
  }

  /**
   * Generate secure storage key with prefix
   */
  private getSecureKey(key: string): string {
    return `tension_secure_${key}`;
  }
}

// Export singleton instance
let secureStorageInstance: SecureStorage | null = null;

export function getSecureStorage(userSecret?: string): SecureStorage {
  if (!secureStorageInstance) {
    secureStorageInstance = new SecureStorage(userSecret);
  }
  return secureStorageInstance;
}

export function resetSecureStorage(userSecret?: string): SecureStorage {
  secureStorageInstance = new SecureStorage(userSecret);
  return secureStorageInstance;
}

export { SecureStorage };